import React, { useEffect, useState } from "react";
import { message, Spin, Pagination } from "antd";
import { Award, ChevronDown } from "lucide-react";
import api from "../../api/promosApi";
import awardApi from "../../api/awardApi";
import inventory from "../../api/inventoryApi";
import CoinBadge from "../../components/coin_badge/CoinBadge";
import usersIcon from "../../assets/icons/users.png";
import usersApi from "../../api/usersApi";
import "../../components/AddUserModal/style.css";

const IMAGE_BASE_URL = "https://api.duotetra.quest/uploads/gems/";
const PROMO_LIMIT = 10;
const AWARD_LIMIT = 10;

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

function promoStatusTone(status) {
    if (status === "ACTIVE") {
        return "border-emerald-400/35 bg-emerald-500/15 text-emerald-100";
    }
    if (status === "COMPLETED" || status === "MANUALLY_COMPLETED") {
        return "border-sky-400/35 bg-sky-500/15 text-sky-100";
    }
    return "border-white/15 bg-white/[0.08] text-white/75";
}

function blockchainStatusBadge(bs) {
    if (!bs) return null;
    const map = {
        funded:          { cls: "border-emerald-400/35 bg-emerald-500/15 text-emerald-300", label: "⛓ Funded" },
        pending_fee:     { cls: "border-yellow-400/35 bg-yellow-500/15 text-yellow-300",   label: "⏳ Fee pending" },
        pending_deposit: { cls: "border-orange-400/35 bg-orange-500/15 text-orange-300",   label: "⏳ Deposit pending" },
        claiming:        { cls: "border-blue-400/35 bg-blue-500/15 text-blue-300",         label: "🎯 Claiming" },
        completed:       { cls: "border-sky-400/35 bg-sky-500/15 text-sky-200",            label: "✅ Done" },
        withdrawn:       { cls: "border-rose-400/35 bg-rose-500/15 text-rose-300",         label: "↩ Withdrawn" },
    };
    const item = map[bs] || { cls: "border-white/15 bg-white/[0.08] text-white/55", label: bs };
    return (
        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${item.cls}`}>
            {item.label}
        </span>
    );
}

export default function AwardsPage() {
    const [promos, setPromos] = useState([]);
    const [promoPage, setPromoPage] = useState(1);
    const [promoTotal, setPromoTotal] = useState(0);
    const [expandedId, setExpandedId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [awards, setAwards] = useState({});
    const [total, setTotal] = useState({});
    const [gemTypes, setGemTypes] = useState([]);
    const [userProfiles, setUserProfiles] = useState({});

    useEffect(() => {
        fetchPromos(promoPage);
        fetchGems();
    }, [promoPage]);

    const fetchPromos = async (page) => {
        setLoading(true);
        try {
            const response = await api.promosApi.getPaginatedPromos({ page, limit: PROMO_LIMIT });
            setPromos(response.data);
            setPromoTotal(response.total || 0);
        } catch (e) {
            console.error(e);
            message.error("Ошибка при загрузке промо");
        } finally {
            setLoading(false);
        }
    };

    const fetchGems = async () => {
        try {
            const data = await inventory.gemCatalogApi.getAllGems();
            setGemTypes(data);
        } catch (e) {
            console.error(e);
            message.error("Ошибка при загрузке гемов");
        }
    };

    const fetchUsersForAwards = async (awardsList) => {
        const missingIds = awardsList.map((a) => a.userId).filter((id) => !userProfiles[id]);
        const uniqueIds = [...new Set(missingIds)];

        try {
            const users = await Promise.all(
                uniqueIds.map(async (id) => {
                    const profile = await usersApi.getProfile(id);
                    return { id, profile };
                })
            );
            setUserProfiles((prev) => {
                const updated = { ...prev };
                for (const { id, profile } of users) {
                    updated[id] = profile;
                }
                return updated;
            });
        } catch (e) {
            console.error(e);
            message.error("Ошибка при загрузке профилей пользователей");
        }
    };

    const toggleExpand = async (promoId) => {
        if (expandedId === promoId) {
            setExpandedId(null);
            return;
        }

        try {
            setExpandedId(promoId);
            const res = await awardApi.getByPromo(promoId, 1, AWARD_LIMIT);
            setAwards((prev) => ({ ...prev, [promoId]: res.data }));
            setTotal((prev) => ({ ...prev, [promoId]: res.total }));

            await fetchUsersForAwards(res.data);
        } catch (err) {
            console.error(err);
            message.error("Ошибка при загрузке наград");
        }
    };

    const changePage = async (promoId, newPage) => {
        try {
            const res = await awardApi.getByPromo(promoId, newPage, AWARD_LIMIT);
            setAwards((prev) => ({ ...prev, [promoId]: res.data }));
            setTotal((prev) => ({ ...prev, [promoId]: res.total }));

            await fetchUsersForAwards(res.data);
        } catch (err) {
            console.error(err);
            message.error("Ошибка при смене страницы");
        }
    };

    const onPromoPageChange = (page) => {
        setExpandedId(null);
        setPromoPage(page);
    };

    const promoPages = Math.max(1, Math.ceil(promoTotal / PROMO_LIMIT) || 1);

    const thClass = "whitespace-nowrap px-4 py-3.5 text-left text-[12px] font-semibold text-white/60";
    const tdClass = "px-4 py-3 text-[13px] text-white/85";

    return (
        <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-16 -top-16 h-36 w-36 rounded-full bg-amber-400/10 blur-3xl" />
                <div className="relative flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                            <Award className="h-5 w-5 text-amber-300/90" strokeWidth={2} />
                        </span>
                        <div className="min-w-0">
                            <h1 className="text-lg font-semibold tracking-tight text-white/90 sm:text-xl">Awards</h1>
                            <p className="text-[12px] text-white/45">
                                Промо с раскрытием списка наград · всего в базе: {promoTotal}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <div
                className={cn(
                    shellClass,
                    "flex shrink-0 flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5"
                )}
            >
                <span className="text-[13px] text-white/50">
                    Страница промо {promoPage} из {promoPages}
                </span>
                <Pagination
                    current={promoPage}
                    total={promoTotal}
                    pageSize={PROMO_LIMIT}
                    onChange={onPromoPageChange}
                    showSizeChanger={false}
                    className="pagination-custom !mb-0 flex flex-wrap justify-end"
                    size="small"
                    showTotal={(t, range) => `${range[0]}-${range[1]} из ${t} промо`}
                />
            </div>

            <section className={cn(shellClass, "flex min-h-0 flex-1 flex-col overflow-hidden")}>
                {loading ? (
                    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 py-16">
                        <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                        <span className="text-[13px] text-white/45">Загрузка промо…</span>
                    </div>
                ) : promos.length === 0 ? (
                    <div className="flex min-h-0 flex-1 items-center justify-center py-16">
                        <p className="text-center text-sm text-white/45">Нет промо</p>
                    </div>
                ) : (
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        <div className="min-h-0 flex-1 overflow-auto">
                            <table className="h-full min-h-full w-full min-w-[900px] border-collapse text-left text-[13px]">
                            <thead className="sticky top-0 z-[1] bg-[#0a0e18]/95 text-white/70 backdrop-blur-md">
                                <tr className="border-b border-white/10">
                                    <th className={thClass}>ID</th>
                                    <th className={thClass}>Token</th>
                                    <th className={thClass}>Promoter</th>
                                    <th className={thClass}>Participants</th>
                                    <th className={thClass}>Category</th>
                                    <th className={thClass}>Status</th>
                                    <th className={thClass}>Blockchain</th>
                                    <th className={cn(thClass, "text-right")}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className="align-top text-white/85">
                                {promos.map((promo) => (
                                    <React.Fragment key={promo.id}>
                                        <tr
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => toggleExpand(promo.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    toggleExpand(promo.id);
                                                }
                                            }}
                                            className={cn(
                                                "cursor-pointer border-b border-white/[0.06] transition-colors hover:bg-white/[0.04]",
                                                expandedId === promo.id && "bg-white/[0.03]"
                                            )}
                                        >
                                            <td className={cn(tdClass, "text-white/55")}>{promo.id}</td>
                                            <td className={cn(tdClass, "w-[200px] max-w-[220px]")}>
                                                <CoinBadge
                                                    icon={promo.tokenType}
                                                    name={promo.tokenType}
                                                    count={promo.totalAmount}
                                                    isCenter
                                                />
                                            </td>
                                            <td className={tdClass}>
                                                <div className="flex max-w-[140px] items-center gap-2">
                                                    {promo.promoters?.[0]?.logo && (
                                                        <img
                                                            src={`https://api.duotetra.quest${promo.promoters[0].logo}`}
                                                            alt=""
                                                            className="h-7 w-7 shrink-0 rounded-full border border-white/10 object-cover"
                                                        />
                                                    )}
                                                    <span className="truncate text-[12px] text-white/65">
                                                        {promo.promoters?.[0]?.name || "—"}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className={tdClass}>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-white/90">{promo.participantsCount}</span>
                                                    <img src={usersIcon} className="h-4 w-4 opacity-80" alt="" />
                                                </div>
                                            </td>
                                            <td className={tdClass}>
                                                <div className="flex items-center gap-2">
                                                    {promo.category?.positions?.length > 0 &&
                                                        gemTypes.length > 0 &&
                                                        (() => {
                                                            const pos = promo.category.positions.length;
                                                            const matchedGem = gemTypes.find((g) => g.position === pos);
                                                            if (!matchedGem) return null;
                                                            return (
                                                                <img
                                                                    src={`${IMAGE_BASE_URL}${matchedGem.frontImage}`}
                                                                    alt=""
                                                                    className="h-6 w-6 object-contain"
                                                                />
                                                            );
                                                        })()}
                                                    <span className="text-white/70">{promo.category?.category_name ?? "—"}</span>
                                                </div>
                                            </td>
                                            <td className={tdClass}>
                                                <span
                                                    className={cn(
                                                        "inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold",
                                                        promoStatusTone(promo.status)
                                                    )}
                                                >
                                                    {promo.status}
                                                </span>
                                            </td>
                                            <td className={tdClass}>
                                                {blockchainStatusBadge(promo.blockchainStatus)}
                                            </td>
                                            <td className={cn(tdClass, "text-right")}>
                                                <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-amber-300/90 hover:text-amber-200">
                                                    <ChevronDown
                                                        className={cn(
                                                            "h-4 w-4 shrink-0 transition-transform duration-200",
                                                            expandedId === promo.id && "rotate-180"
                                                        )}
                                                    />
                                                    {expandedId === promo.id ? "Скрыть" : "Награды"}
                                                </span>
                                            </td>
                                        </tr>

                                        {expandedId === promo.id && (
                                            <tr className="border-b border-white/[0.06] bg-white/[0.02]">
                                                <td colSpan={7} className="p-4 sm:p-5">
                                                    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                                                        <h4 className="mb-3 text-[14px] font-semibold text-white/90">
                                                            Награды
                                                            <span className="ml-2 text-[12px] font-normal text-white/40">
                                                                (последние {AWARD_LIMIT})
                                                            </span>
                                                        </h4>
                                                        <div className="overflow-x-auto rounded-lg border border-white/[0.08]">
                                                            <table className="w-full min-w-[640px] border-collapse text-left text-[13px]">
                                                                <thead className="bg-white/[0.05] text-[12px] text-white/55">
                                                                    <tr className="border-b border-white/10">
                                                                        <th className="px-3 py-2.5 font-semibold">ID</th>
                                                                        <th className="px-3 py-2.5 font-semibold">User</th>
                                                                        <th className="px-3 py-2.5 font-semibold">Gem</th>
                                                                        <th className="px-3 py-2.5 font-semibold">Amount</th>
                                                                        <th className="px-3 py-2.5 font-semibold">Status</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {awards[promo.id]?.map((a) => {
                                                                        const gem = gemTypes.find((g) => g.id === a.gemId);
                                                                        const user = userProfiles[a.userId];
                                                                        return (
                                                                            <tr
                                                                                key={a.id}
                                                                                className="border-t border-white/[0.06] transition-colors hover:bg-white/[0.03]"
                                                                            >
                                                                                <td className="px-3 py-2.5 text-white/55">
                                                                                    {a.id}
                                                                                </td>
                                                                                <td className="px-3 py-2.5 text-white/85">
                                                                                    {user ? (
                                                                                        `${user.firstName} ${user.lastName}`
                                                                                    ) : (
                                                                                        <span className="italic text-white/40">
                                                                                            …
                                                                                        </span>
                                                                                    )}
                                                                                </td>
                                                                                <td className="px-3 py-2.5">
                                                                                    {gem ? (
                                                                                        <div className="flex items-center gap-2">
                                                                                            <img
                                                                                                src={`${IMAGE_BASE_URL}${gem.frontImage}`}
                                                                                                alt=""
                                                                                                className="h-7 w-7 object-contain"
                                                                                            />
                                                                                            <span className="text-white/80">
                                                                                                {gem.name}
                                                                                            </span>
                                                                                        </div>
                                                                                    ) : (
                                                                                        <span className="italic text-white/40">
                                                                                            unknown
                                                                                        </span>
                                                                                    )}
                                                                                </td>
                                                                                <td className="px-3 py-2.5 font-medium text-white/90">
                                                                                    {a.amount}
                                                                                </td>
                                                                                <td className="px-3 py-2.5">
                                                                                    <span className="rounded-md border border-white/10 bg-white/[0.06] px-2 py-0.5 text-[11px] text-white/70">
                                                                                        {a.status}
                                                                                    </span>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        {total[promo.id] > AWARD_LIMIT && (
                                                            <div className="mt-4 flex justify-end border-t border-white/[0.06] pt-3">
                                                                <Pagination
                                                                    size="small"
                                                                    pageSize={AWARD_LIMIT}
                                                                    total={total[promo.id]}
                                                                    onChange={(p) => changePage(promo.id, p)}
                                                                    className="pagination-custom"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
